import { useState } from "react";
import {
  MicIcon,
  MicOffIcon,
  PauseIcon,
  PlayIcon,
  WifiOffIcon,
  AlertTriangleIcon,
} from "./Icons";
import { LoadingDots } from "./LoadingDots";
import { DeviceSelector } from "./DeviceSelector";
import {
  ConversationStatusIndicator,
  type ConversationStatus,
} from "./ConversationStatus";
import { strings } from "../strings";

export type ConnectionState =
  | "idle"
  | "initializing"
  | "connecting"
  | "connected"
  | "ready"
  | "disconnected"
  | "error";

export type NetworkQualityState = "unknown" | "good" | "warning" | "bad";

interface LiveControlsProps {
  connectionState: ConnectionState;
  conversationStatus: ConversationStatus;
  isMicEnabled: boolean;
  onToggleMic: () => void;
  onPause: () => void;
  onReconnect?: () => void;
  onMicrophoneChange?: (deviceId: string) => void;
  onSpeakerChange?: (deviceId: string) => void;
  audioTrack?: MediaStreamTrack | null;
  networkQualityState?: NetworkQualityState;
  disabled?: boolean;
  className?: string;
}

/**
 * Live recording controls - matches web app styling exactly
 */
export function LiveControls({
  connectionState,
  conversationStatus,
  isMicEnabled,
  onToggleMic,
  onPause,
  onReconnect,
  onMicrophoneChange,
  onSpeakerChange,
  audioTrack = null,
  networkQualityState = "unknown",
  disabled = false,
  className,
}: LiveControlsProps) {
  const [hasMics, setHasMics] = useState(true);
  const [hasPermission, setHasPermission] = useState(true);

  const isConnected =
    connectionState === "connected" || connectionState === "ready";
  const isLoading =
    connectionState === "initializing" || connectionState === "connecting";

  const handleDeviceAvailabilityChange = (
    micsAvailable: boolean,
    _speakersAvailable: boolean,
    permissionGranted: boolean,
  ) => {
    setHasMics(micsAvailable);
    setHasPermission(permissionGranted);
  };

  const iconButtonBase =
    "inline-flex items-center justify-center w-12 h-12 rounded-full border border-border bg-card cursor-pointer text-sm font-medium transition-all shadow-sm outline-none";
  const iconButtonDisabled = disabled
    ? "opacity-50 pointer-events-none cursor-not-allowed"
    : "";

  return (
    <div className={`flex flex-col gap-4 ${className || ""}`}>
      {isLoading ? (
        // Loading/Connecting state
        <div className="flex flex-col items-center gap-2">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <LoadingDots size="sm" />
            <span>{strings.liveControls.connecting}</span>
          </div>
        </div>
      ) : isConnected ? (
        // Connected/Recording state
        <>
          {/* Control buttons */}
          <div className="flex items-center justify-center gap-4 relative z-10">
            {/* Network Quality Warning Indicator */}
            {(networkQualityState === "bad" ||
              networkQualityState === "warning") && (
              <button
                type="button"
                className={`${iconButtonBase} bg-amber-100 border-amber-300 cursor-default`}
                title={strings.liveControls.networkWarning}
              >
                <WifiOffIcon className="w-5 h-5 text-amber-600" />
              </button>
            )}

            {/* Microphone controls group - pill behind circle */}
            <div className="relative flex items-center overflow-visible">
              {/* Device Selector Dropdown - pill sticking out behind mic button */}
              <div className="absolute right-3 z-0">
                <DeviceSelector
                  onMicrophoneChange={onMicrophoneChange}
                  onSpeakerChange={onSpeakerChange}
                  onAvailabilityChange={handleDeviceAvailabilityChange}
                />
              </div>

              {/* Warning badge for device selector */}
              {(!hasMics || !hasPermission) && (
                <div className="absolute right-0 -top-1 w-5 h-5 rounded-full bg-destructive flex items-center justify-center z-20">
                  <span className="text-white text-xs font-bold">!</span>
                </div>
              )}

              {/* Mute/Unmute Button - on top */}
              <div className="relative z-10 ml-10">
                {/* Opaque layer to cover pill button behind */}
                <div className="absolute inset-0 bg-card rounded-full -z-10" />

                <button
                  type="button"
                  onClick={onToggleMic}
                  disabled={!hasMics || !hasPermission || disabled}
                  title={
                    !hasMics || !hasPermission
                      ? strings.liveControls.permissionNeeded
                      : isMicEnabled
                        ? strings.liveControls.muteMic
                        : strings.liveControls.unmuteMic
                  }
                  className={`${iconButtonBase} ${
                    !isMicEnabled
                      ? "bg-destructive/10 border-destructive"
                      : "bg-card border-border"
                  } ${!hasMics || !hasPermission || disabled ? "opacity-50 pointer-events-none" : ""}`}
                >
                  {isMicEnabled ? (
                    <MicIcon className="w-5 h-5" />
                  ) : (
                    <MicOffIcon className="w-5 h-5 text-destructive" />
                  )}
                </button>

                {/* Warning badge for no devices */}
                {!hasMics && (
                  <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-destructive flex items-center justify-center z-50">
                    <AlertTriangleIcon className="w-3 h-3 text-white" />
                  </div>
                )}
              </div>
            </div>

            {/* End Interview Button */}
            <button
              type="button"
              onClick={onPause}
              disabled={disabled}
              title={strings.liveControls.pauseInterview}
              className={`${iconButtonBase} ${iconButtonDisabled}`}
            >
              <PauseIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Conversation status - with reserved space */}
          <div className="flex justify-center min-h-10">
            <ConversationStatusIndicator
              status={conversationStatus}
              audioTrack={audioTrack}
            />
          </div>
        </>
      ) : connectionState === "error" && onReconnect ? (
        // Error state with reconnect option
        <div className="flex flex-col items-center gap-4">
          <button
            type="button"
            onClick={onReconnect}
            disabled={disabled}
            title={strings.liveControls.reconnect}
            className={`inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary text-primary-foreground border-none cursor-pointer text-sm font-medium transition-all shadow-sm outline-none ${iconButtonDisabled}`}
          >
            <PlayIcon className="ml-1 w-6 h-6" />
          </button>
          <p className="text-sm text-muted-foreground">
            {strings.liveControls.connectionLost}
          </p>
        </div>
      ) : (
        // Disconnected state (no reconnect available)
        <div className="text-center">
          <p className="text-sm text-muted-foreground">
            {connectionState === "error"
              ? strings.liveControls.connectionError
              : strings.liveControls.disconnected}
          </p>
        </div>
      )}
    </div>
  );
}
